/* eslint-disable import/no-cycle */
import {computed, decorate, observable} from 'mobx';
import {get} from 'lodash';
import moment from 'moment';
import {Api} from '../middleware/api';
import {EntityStore} from './entityStore';
import {OperationStateLog} from '../models/operationStateLog';
import {sortChronologically} from '../components/shared/tables/sorters';
import {round} from '../utils/number';

/**
 *
 * @param {Array<OperationStateLog>} logs
 * @param {number} factor : factor is derived from the speed denominator to match the timescale of the x values
 * @param {number} offset
 * @returns {Array<[number ,number]>} : Return series data of planned line
 */
const convertToPlannedData = (logs, factor, offset) => {
  const plannedQuantity = get(logs, [0, 'operation', 'order', 'plannedQuantity']);
  const plannedData = [];

  logs.forEach((log, index) => {
    const startTime = new Date(log.start).getTime() + offset * 1000;
    const endTime = new Date(log.end).getTime() + offset * 1000;
    if (!index) {
      plannedData.push([startTime, 0]);
    } else if (log.state?.isInterruption) {
      const prevEntry = plannedData[plannedData.length - 1];
      const quantity = prevEntry[1] + ((startTime - prevEntry[0]) * log.operation?.plannedSpeed) / factor;

      plannedData.push([startTime, round(quantity, 2)]);

      if (log.end) {
        plannedData.push([endTime, round(quantity, 2)]);
      }
    }
  });

  if (plannedData.length) {
    const plannedSpeed = get(logs, [0, 'operation', 'plannedSpeed']);
    const latestEntry = plannedData[plannedData.length - 1];
    const remainingQuantity = Math.abs(plannedQuantity - latestEntry[1]);
    const remainingDuration = (remainingQuantity * factor) / plannedSpeed;

    plannedData.push([latestEntry[0] + remainingDuration, round(plannedQuantity, 2)]);
  }

  return plannedData;
};

export class OperationStateLogStore extends EntityStore {
  logs = [];
  selected = null;

  constructor(rootStore) {
    super(rootStore, 'logs', Api.operationStateLogs, OperationStateLog);
  }

  loadManyByWorkplace(workplaceId, {fromDate, toDate, isInterruption, interruptionReasonId}) {
    return this.loadAll({
      params: {
        workplaceId,
        fromDate: fromDate.toDate(),
        toDate: toDate.toDate(),
        isInterruption,
        interruptionReasonId,
      },
    }).then((results) => {
      this.rootStore.operationStore.loadMany(results.map((log) => log.operationId)).then(() => {
        this.logs.forEach((log) => {
          log.operation = this.rootStore.operationStore.getById(log.operationId);
        });
      });

      return results;
    });
  }

  loadByOperation(operationId) {
    return this.loadAllWithDependencies({params: {operationId}});
  }

  getByWorkplaceId(workplaceId) {
    return this.logs.filter((log) => log.operation?.workplaceId === workplaceId);
  }

  getLatestOfWorkplace(workplaceId) {
    const wpLogs = this.getByWorkplaceId(workplaceId);
    return wpLogs.reduce((newest, next) => {
      if (!newest || newest.start < next.start) {
        return next;
      }
      return newest;
    }, null);
  }

  getByStateIdAndWorkplaceId(stateId, workplaceId) {
    return this.logs.filter((log) => log.stateId === stateId && log.operation?.workplaceId === workplaceId);
  }

  getLatestByStateAndWorkplace(stateId, workplaceId) {
    const wpLogs = this.getByStateIdAndWorkplaceId(stateId, workplaceId);
    return wpLogs.reduce((newest, next) => {
      if (!newest || newest.start < next.start) {
        return next;
      }
      return newest;
    }, null);
  }

  getUnclassifiedByWorkplaceId(workplaceId) {
    return this.logs.filter(
      (log) => log.operation?.workplaceId === workplaceId && log.state?.isInterruption && !log.interruptionReasonId
    );
  }

  getPlannedDataByWorkplaceId(workplaceId, orderId, factor, offset = 0) {
    if (!workplaceId) {
      return [];
    }
    const logs = this.logs
      .filter((log) => log.operation?.id === workplaceId && log.operation.orderId === orderId)
      .sort((a, b) => sortChronologically(a.start, b.start));
    return convertToPlannedData(logs, factor, offset);
  }

  getInterruptionsByOperationIds(operationIds) {
    return this.logs.filter((log) =>
      operationIds.find((operationId) => log.operationId === operationId) && log.state?.isInterruption)
      .map((log) => {
        log.interruptionDuration = moment(log.end).diff(moment(log.start), 'seconds');
        return log;
      });
  }

  get latestBySelectedWorkplace() {
    if (this.rootStore.workplaceStore.selectedWorkplace) {
      const opLogs = this.getByWorkplaceId(this.rootStore.workplaceStore.selectedWorkplace.id);
      return opLogs.reduce((newest, next) => {
        if (!newest || newest.start < next.start) {
          return next;
        }
        return newest;
      }, null);
    }
    return null;
  }

  getByWorkplaceIdIgnoreShortInterruptions(workplaceId, minInterruptionDuration) {
    if (minInterruptionDuration > 0) {
      return this.logs.filter((log) =>
        log.operation?.workplaceId === workplaceId
        && (!log.state.isInterruption || !log.end
          || moment(log.end).diff(moment(log.start), 'seconds') > minInterruptionDuration
        ))
        .sort((a, b) => moment(a.start) - moment(b.start))
        .map((log, index, logs) => {
          const current = {...log};
          let i = index + 1;
          let next = logs[i];
          while (next && log.stateId === next.stateId) {
            current.end = next.end;
            next.remove = true;
            next = logs[i++]; // eslint-disable-line no-plusplus
          }
          return current;
        }).filter((log) => !log.remove);
    }
    return this.logs.filter((log) => log.operation?.workplaceId === workplaceId);
  }
}

decorate(OperationStateLogStore, {
  selected: observable,
  logs: observable,
  latestBySelectedWorkplace: computed,
});
